<template>
  <div :dir="isRtl != false ? 'rtl' : 'ltr'" class="w-full">
    <div class="rounded-2xl border border-base-300/80 bg-gradient-to-b from-base-100 to-base-200/30 px-5 py-6 shadow-sm md:px-7 md:py-7">
      <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <p class="max-w-2xl text-sm leading-relaxed text-base-content/80" :class="isRtl === true ? 'text-right' : 'text-left'">{{ t('deploymentChannels.inline.description', 'Choose where this agent should be available and connect the channels that need extra setup.') }}</p>
        <div class="mt-5 shrink-0 lg:mt-0">
          <div class="rounded-2xl border border-primary/20 bg-primary/5 px-5 py-3 text-center shadow-sm">
            <div class="text-3xl font-bold tabular-nums leading-none text-primary">{{ selectedSummaryChannels.length }}</div>
            <div class="mt-1.5 text-xs font-medium uppercase tracking-wide text-base-content/60">{{ t('deploymentChannels.inline.activeCount', 'active channels') }}</div>
          </div>
        </div>
      </div>

      <div v-if="selectedSummaryChannels.length > 0" class="mt-6">
        <div class="text-xs font-medium uppercase tracking-wide text-base-content opacity-60" :class="isRtl === true ? 'text-right' : 'text-left'">{{ t('deploymentChannels.inline.activeNow', 'Active now') }}</div>
        <div class="channel-chip-list mt-2">
          <span v-for="channel in selectedSummaryChannels" :key="channel.id" class="channel-chip">
            <Icon :icon="channel.icon" class="w-4 h-4" />
            <span>{{ channel.displayName }}</span>
            <span v-if="(channel.id === 'Whatsapp' || channel.id === 'Whatsapp Voice') && selectedWhatsappDisplay.length > 0" class="opacity-70">{{ selectedWhatsappDisplay }}</span>
            <span v-if="channel.id === 'Phone' && selectedPhoneDisplay.length > 0" class="opacity-70">{{ selectedPhoneDisplay }}</span>
          </span>
        </div>
      </div>

      <div v-else class="mt-5 rounded-2xl border border-dashed border-base-300 bg-base-200/40 px-4 py-5 text-sm text-base-content opacity-70" :class="isRtl === true ? 'text-right' : 'text-left'">
        {{ t('deploymentChannels.inline.emptyState', 'No deployment channels are active yet. Start by enabling the channels you want this agent to use.') }}
      </div>

      <div class="flex flex-wrap -m-3 mt-8">
        <div v-for="channel in availableChannels" :key="channel.id" class="w-full md:w-1/2 xl:w-1/3 p-3">
          <div class="flex h-full min-h-[160px] flex-col rounded-2xl border px-5 py-5 transition-all duration-200 ease-out" :class="getCardClass(channel.id)">
            <div class="flex flex-1 flex-col">
              <div class="flex items-start">
                <span class="mr-2 ml-2 inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-base-200/90 ring-1 ring-base-300/50" :class="isSelected(channel.id) === true ? 'bg-primary/10 ring-primary/25' : ''">
                  <Icon :icon="channel.icon" class="h-5 w-5" :class="isSelected(channel.id) === true ? 'text-primary' : 'text-base-content'" />
                </span>
                <div class="min-w-0 flex-1" :class="isRtl === true ? 'mr-4 text-right' : 'ml-4 text-left'">
                  <div class="flex items-start justify-between" :class="isRtl === true ? 'flex-row-reverse' : ''">
                    <h4 class="pr-3 text-sm font-semibold leading-snug">{{ channel.displayName }}</h4>
                    <span class="badge badge-sm shrink-0 whitespace-nowrap" :class="[getBadgeClass(channel.id), isRtl === true ? 'mr-3' : 'ml-3']">{{ getStatusLabel(channel.id) }}</span>
                  </div>
                  <p class="mt-1 text-xs leading-relaxed text-base-content/70">{{ channel.description }}</p>
                </div>
              </div>

              <div v-if="(channel.id === 'Whatsapp' || channel.id === 'Whatsapp Voice') && selectedWhatsappDisplay.length > 0 && isSelected(channel.id) === true" class="mt-3 rounded-lg border border-base-300/60 bg-base-200/50 px-2 py-1.5 font-mono text-xs">
                {{ selectedWhatsappDisplay }}
              </div>
              <div v-if="channel.id === 'Phone' && selectedPhoneDisplay.length > 0 && isSelected(channel.id) === true" class="mt-3 rounded-lg border border-base-300/60 bg-base-200/50 px-2 py-1.5 font-mono text-xs">
                {{ selectedPhoneDisplay }}
              </div>
            </div>

            <div class="mt-5 flex flex-col pt-4">
              <div class="flex items-center" :class="isRtl === true ? 'space-x-reverse space-x-2' : 'space-x-2'">
                <button v-if="channel.id === 'Whatsapp' || channel.id === 'Whatsapp Voice'" type="button" class="btn btn-sm flex-1" :disabled="isChannelEnabled(channel.id) !== true" :title="isChannelEnabled(channel.id) === true ? '' : t('deploymentChannels.common.locked', 'Not approved for this organization')" @click="isChannelEnabled(channel.id) === true ? openWhatsAppSelection(channel.id) : null">
                  {{ isSelected(channel.id) === true && selectedWhatsappSourceId !== null ? t('deploymentChannels.whatsapp.change','Change Number') : t('deploymentChannels.whatsapp.connect','Connect WhatsApp') }}
                </button>
                <button v-else-if="channel.id === 'Phone'" type="button" class="btn btn-sm flex-1" :disabled="isChannelEnabled(channel.id) !== true" :title="isChannelEnabled(channel.id) === true ? '' : t('deploymentChannels.common.locked', 'Not approved for this organization')" @click="isChannelEnabled(channel.id) === true ? openPhoneSelection() : null">
                  {{ isSelected(channel.id) === true && selectedPhoneSourceId !== null ? t('deploymentChannels.phone.change','Change Number') : t('deploymentChannels.phone.connect','Connect Phone') }}
                </button>
                <button v-else-if="channel.id === 'Email'" type="button" class="btn btn-sm flex-1" :disabled="isChannelEnabled(channel.id) !== true" :title="isChannelEnabled(channel.id) === true ? '' : t('deploymentChannels.common.locked', 'Not approved for this organization')" @click="isChannelEnabled(channel.id) === true ? openEmailInfo() : null">
                  {{ t('deploymentChannels.email.contactSupportButton','Contact Support') }}
                </button>
                <button v-else type="button" class="btn btn-sm flex-1" :disabled="isChannelEnabled(channel.id) !== true" :title="isChannelEnabled(channel.id) === true ? '' : t('deploymentChannels.common.locked', 'Not approved for this organization')" @click="toggleChannel(channel.id)">
                  {{ isSelected(channel.id) === true ? t('deploymentChannels.common.deactivate','Deactivate') : channel.id === 'Web' ? t('deploymentChannels.web.connect','Enable Web') : t('deploymentChannels.common.activate','Activate') }}
                </button>

                <button v-if="channel.id === 'Whatsapp' && selectedWhatsappSourceId !== null && isSelected(channel.id) === true && isChannelEnabled(channel.id) === true" type="button" class="btn btn-square btn-ghost btn-sm text-error hover:bg-error/10" @click="unbindWhatsapp()" :title="t('deploymentChannels.whatsapp.unbind','Disconnect WhatsApp')">
                  <Icon icon="tabler:trash" class="h-4 w-4" />
                </button>
                <button v-if="channel.id === 'Phone' && selectedPhoneSourceId !== null && isSelected(channel.id) === true && isChannelEnabled(channel.id) === true" type="button" class="btn btn-square btn-ghost btn-sm text-error hover:bg-error/10" @click="onPhoneConfirm({ sourceId: null })" :title="t('deploymentChannels.phone.unbind','Disconnect Phone')">
                  <Icon icon="tabler:trash" class="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <WhatsAppNumberPickerModal :visible="showWhatsAppModal" :items="availableWhatsapp" :loading="loadingNumbers" :currentId="selectedWhatsappSourceId" @close="cancelWhatsApp" @confirm="onWhatsAppConfirm" />

  <WhatsAppNumberPickerModal :visible="showPhoneModal" :items="availablePhone" :loading="loadingPhone" :currentId="selectedPhoneSourceId" mode="phone" @close="cancelPhone" @confirm="onPhoneConfirm" />

  <transition>
    <div v-if="showEmailModal === true" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div class="w-full max-w-md rounded-lg bg-base-100 p-6 shadow-lg dark:bg-gray-800">
        <div class="flex items-center mb-4">
          <span class="inline-flex w-9 h-9 rounded-lg items-center justify-center bg-base-300 dark:bg-gray-700" :class="isRtl === true ? 'ml-3' : 'mr-3'">
            <Icon icon="tabler:mail" class="w-5 h-5 text-base-content" />
          </span>
          <h3 class="text-lg font-semibold">{{ t('deploymentChannels.email.title','Email') }}</h3>
        </div>
        <p class="text-sm mb-6">{{ t('deploymentChannels.email.contactSupport','Please contact support for email setup.') }}</p>
        <div class="flex justify-end">
          <button type="button" class="btn" @click="closeEmailInfo">{{ t('common.close','Close') }}</button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { Icon } from '@iconify/vue/dist/offline';
import { useI18n } from '@/i18n';
import axios from '@/api/axiosInstance';
import { useToast } from 'vue-toastification';
import { globalState } from '@/globalState';
import WhatsAppNumberPickerModal from '@/components/WhatsAppNumberPickerModal.vue';

const props = defineProps({
  selectedChannels: {
    type: Array,
    default: () => []
  },
  agentId: {
    type: String,
    default: ''
  },
  whatsappSourceId: {
    type: Number,
    default: null
  },
  whatsappNumber: {
    type: String,
    default: ''
  },
  phoneSourceId: {
    type: Number,
    default: null
  },
  phoneNumber: {
    type: String,
    default: ''
  },
  enabledChannels: {
    type: Array,
    default: null
  },
  allowedChannels: {
    type: Array,
    default: null
  }
});

const emit = defineEmits(['update:selectedChannels', 'update:whatsappSourceId', 'update:phoneSourceId']);

const { t, currentLanguage } = useI18n();
const isRtl = computed(() => (['he', 'ar'].includes(currentLanguage.value) === true) ? true : false);
const toast = useToast();
const phoneSourceTypes = ['twilioPhone', 'phonePartner', 'phoneCustomSip', 'sipTrunk', 'sonarixPhone', 'genovisePhone', 'phone'];

function normalizeSourceId(value) {
  if (value === null || value === undefined) {
    return null;
  }
  const parsed = Number(value);
  if (Number.isFinite(parsed) !== true) {
    return null;
  }
  return Math.floor(parsed);
}

function isSameSourceId(left, right) {
  const normalizedLeft = normalizeSourceId(left);
  const normalizedRight = normalizeSourceId(right);
  if (normalizedLeft === null || normalizedRight === null) {
    return false;
  }
  return normalizedLeft === normalizedRight;
}

const rawAvailableChannels = ref([
  {
    id: 'Web',
    displayName: t('deploymentChannels.channels.web.name', 'Web'),
    description: t('deploymentChannels.channels.web.desc', 'Embed or website widget'),
    icon: 'heroicons:globe-alt'
  },
  {
    id: 'Web Voice Agent',
    displayName: t('deploymentChannels.channels.webVoiceAgent.name', 'Web Voice Agent'),
    description: t('deploymentChannels.channels.webVoiceAgent.desc', 'Voice agent in the web chat'),
    icon: 'tabler:microphone'
  },
  {
    id: 'Whatsapp',
    displayName: t('deploymentChannels.channels.whatsapp.name', 'Whatsapp'),
    description: t('deploymentChannels.channels.whatsapp.desc', 'WhatsApp messaging'),
    icon: 'tabler:brand-whatsapp'
  },
  {
    id: 'Whatsapp Voice',
    displayName: t('deploymentChannels.channels.whatsappVoice.name', 'WhatsApp Voice'),
    description: t('deploymentChannels.channels.whatsappVoice.desc', 'AI voice calls through WhatsApp'),
    icon: 'tabler:brand-whatsapp'
  },
  {
    id: 'Phone',
    displayName: t('deploymentChannels.channels.phone.name', 'Phone'),
    description: t('deploymentChannels.channels.phone.desc', 'Phone support'),
    icon: 'tabler:phone'
  },
  {
    id: 'Email',
    displayName: t('deploymentChannels.channels.email.name', 'Email'),
    description: t('deploymentChannels.channels.email.desc', 'Email channel'),
    icon: 'tabler:mail'
  }
]);

const availableChannels = computed(() => {
  if (Array.isArray(props.allowedChannels) === true && props.allowedChannels.length > 0) {
    return rawAvailableChannels.value.filter(channel => props.allowedChannels.includes(channel.id) === true);
  }
  return rawAvailableChannels.value;
});

function isChannelEnabled(id) {
  if (Array.isArray(props.enabledChannels) !== true) {
    return true;
  }
  return props.enabledChannels.includes(id) === true;
}

function isSelected(id) {
  if (Array.isArray(props.selectedChannels) !== true) {
    return false;
  }
  return props.selectedChannels.includes(id) === true;
}

const selectedSummaryChannels = computed(() => {
  const selected = Array.isArray(props.selectedChannels) === true ? props.selectedChannels : [];
  return availableChannels.value.filter(channel => selected.includes(channel.id) === true);
});

function getStatusLabel(id) {
  if (isChannelEnabled(id) !== true) {
    return t('deploymentChannels.inline.locked', 'Locked');
  }
  if (isSelected(id) === true) {
    return t('deploymentChannels.inline.active', 'Active');
  }
  return t('deploymentChannels.inline.inactive', 'Inactive');
}

function getBadgeClass(id) {
  if (isChannelEnabled(id) !== true) {
    return 'badge-ghost';
  }
  if (isSelected(id) === true) {
    return 'badge-success';
  }
  return 'badge-outline';
}

function getCardClass(id) {
  if (isChannelEnabled(id) !== true) {
    return 'border-base-300/80 bg-base-200/50 opacity-70';
  }
  if (isSelected(id) === true) {
    return 'border-primary/40 bg-gradient-to-b from-primary/8 to-base-100 shadow-md ring-1 ring-primary/15';
  }
  return 'border-base-300/70 bg-base-100 hover:border-primary/35 hover:shadow-md';
}

const selectedWhatsappDisplayOverride = ref('');
const selectedWhatsappDisplay = computed(() => {
  if (typeof selectedWhatsappDisplayOverride.value === 'string' && selectedWhatsappDisplayOverride.value.trim().length > 0) {
    return selectedWhatsappDisplayOverride.value;
  }
  const item = availableWhatsapp.value.find(entry => isSameSourceId(entry.sourceId, selectedWhatsappSourceId.value) === true);
  if (item !== undefined) {
    if (item.number !== undefined && item.number !== null && String(item.number).length > 0) {
      return item.number;
    }
    if (item.display !== undefined && item.display !== null && String(item.display).length > 0) {
      return item.display;
    }
  }
  if (typeof props.whatsappNumber === 'string' && props.whatsappNumber.trim().length > 0) {
    return props.whatsappNumber;
  }
  return '';
});

const selectedPhoneDisplayOverride = ref('');
const selectedPhoneDisplay = computed(() => {
  if (typeof selectedPhoneDisplayOverride.value === 'string' && selectedPhoneDisplayOverride.value.trim().length > 0) {
    return selectedPhoneDisplayOverride.value;
  }
  const item = availablePhone.value.find(entry => isSameSourceId(entry.sourceId, selectedPhoneSourceId.value) === true);
  if (item !== undefined) {
    if (item.number !== undefined && item.number !== null && String(item.number).length > 0) {
      return item.number;
    }
    if (item.display !== undefined && item.display !== null && String(item.display).length > 0) {
      return item.display;
    }
  }
  if (typeof props.phoneNumber === 'string' && props.phoneNumber.trim().length > 0) {
    return props.phoneNumber;
  }
  return '';
});

async function toggleChannel(id) {
  if (isChannelEnabled(id) !== true) {
    return;
  }
  const current = Array.isArray(props.selectedChannels) === true ? props.selectedChannels.slice() : [];
  const exists = current.includes(id) === true;
  if (exists === true) {
    if (id === 'Whatsapp') {
      if (current.includes('Whatsapp Voice') !== true) {
        selectedWhatsappSourceId.value = null;
        emit('update:whatsappSourceId', null);
      }
    }
    if (id === 'Whatsapp Voice') {
      if (current.includes('Whatsapp') !== true) {
        selectedWhatsappSourceId.value = null;
        emit('update:whatsappSourceId', null);
      }
    }
    if (id === 'Phone') {
      selectedPhoneSourceId.value = null;
      emit('update:phoneSourceId', null);
    }
    const index = current.indexOf(id);
    if (index > -1) {
      current.splice(index, 1);
    }
  } else {
    if (id === 'Whatsapp' || id === 'Whatsapp Voice') {
      await openWhatsAppSelection(id);
      return;
    }
    if (id === 'Phone') {
      await openPhoneSelection();
      return;
    }
    current.push(id);
  }
  emit('update:selectedChannels', current);
}

const showWhatsAppModal = ref(false);
const availableWhatsapp = ref([]);
const selectedWhatsappSourceId = ref(normalizeSourceId(props.whatsappSourceId));
const pendingWhatsappChannel = ref('Whatsapp');
const loadingNumbers = ref(false);
const showPhoneModal = ref(false);
const showEmailModal = ref(false);
const availablePhone = ref([]);
const loadingPhone = ref(false);
const selectedPhoneSourceId = ref(normalizeSourceId(props.phoneSourceId));

async function openWhatsAppSelection(channelId = 'Whatsapp') {
  pendingWhatsappChannel.value = channelId === 'Whatsapp Voice' ? 'Whatsapp Voice' : 'Whatsapp';
  if (isChannelEnabled(pendingWhatsappChannel.value) !== true) {
    return;
  }
  showWhatsAppModal.value = true;
  loadingNumbers.value = true;
  try {
    const response = await axios.get(`${globalState.apiEndpoint}/api/communicationSources/available`, { params: { type: 'whatsapp' } });
    const raw = Array.isArray(response.data) === true ? response.data : [];
    const filtered = raw.filter(item => {
      if (pendingWhatsappChannel.value === 'Whatsapp Voice') {
        return item.type === 'whatsappMeta' && item.voiceEnabled === true;
      }
      return item.type === 'whatsappMeta' || item.type === 'whatsappSmooch' || item.type === 'whatsapp99Digital';
    });
    availableWhatsapp.value = filtered.map(item => {
      const sourceIdRaw = item.sourceId !== undefined && item.sourceId !== null ? item.sourceId : (item.sourceid !== undefined && item.sourceid !== null ? item.sourceid : (item.id !== undefined && item.id !== null ? item.id : null));
      const sourceId = normalizeSourceId(sourceIdRaw);
      return {
        sourceId: sourceId,
        type: item.type,
        display: item.display !== undefined && item.display !== null ? item.display : (item.name !== undefined && item.name !== null ? item.name : ''),
        number: item.number !== undefined && item.number !== null ? item.number : ''
      };
    }).filter(item => item.sourceId !== null);
    const currentId = selectedWhatsappSourceId.value !== null ? selectedWhatsappSourceId.value : normalizeSourceId(props.whatsappSourceId);
    if (currentId !== null) {
      const exists = availableWhatsapp.value.some(item => isSameSourceId(item.sourceId, currentId) === true);
      if (exists === false && pendingWhatsappChannel.value !== 'Whatsapp Voice') {
        availableWhatsapp.value.unshift({
          sourceId: currentId,
          type: '',
          display: typeof props.whatsappNumber === 'string' && props.whatsappNumber.length > 0 ? props.whatsappNumber : String(currentId),
          number: typeof props.whatsappNumber === 'string' && props.whatsappNumber.length > 0 ? props.whatsappNumber : ''
        });
      }
    }
  } catch (error) {
    availableWhatsapp.value = [];
  } finally {
    loadingNumbers.value = false;
  }
}

function cancelWhatsApp() {
  showWhatsAppModal.value = false;
}

async function openPhoneSelection() {
  if (isChannelEnabled('Phone') !== true) {
    return;
  }
  showPhoneModal.value = true;
  loadingPhone.value = true;
  try {
    const response = await axios.get(`${globalState.apiEndpoint}/api/communicationSources/available`, { params: { type: 'phoneNumber' } });
    const raw = Array.isArray(response.data) === true ? response.data : [];
    const filtered = raw.filter(item => phoneSourceTypes.includes(String(item.type)) === true);
    availablePhone.value = filtered.map(item => {
      const sourceIdRaw = item.sourceId !== undefined && item.sourceId !== null ? item.sourceId : (item.sourceid !== undefined && item.sourceid !== null ? item.sourceid : (item.id !== undefined && item.id !== null ? item.id : null));
      const sourceId = normalizeSourceId(sourceIdRaw);
      return {
        sourceId: sourceId,
        type: item.type,
        display: item.display !== undefined && item.display !== null ? item.display : (item.name !== undefined && item.name !== null ? item.name : ''),
        number: item.number !== undefined && item.number !== null ? item.number : ''
      };
    }).filter(item => item.sourceId !== null);
    const currentId = selectedPhoneSourceId.value !== null ? selectedPhoneSourceId.value : normalizeSourceId(props.phoneSourceId);
    if (currentId !== null) {
      const exists = availablePhone.value.some(item => isSameSourceId(item.sourceId, currentId) === true);
      if (exists === false) {
        availablePhone.value.unshift({
          sourceId: currentId,
          type: '',
          display: typeof props.phoneNumber === 'string' && props.phoneNumber.length > 0 ? props.phoneNumber : String(currentId),
          number: typeof props.phoneNumber === 'string' && props.phoneNumber.length > 0 ? props.phoneNumber : ''
        });
      }
    }
  } catch (error) {
    availablePhone.value = [];
  } finally {
    loadingPhone.value = false;
  }
}

function cancelPhone() {
  showPhoneModal.value = false;
}

async function onPhoneConfirm(payload) {
  if (isChannelEnabled('Phone') !== true) {
    showPhoneModal.value = false;
    return;
  }
  if (payload == null) {
    return;
  }
  const rawSourceId = payload.sourceId;
  if (rawSourceId === null || rawSourceId === undefined || Number.isFinite(Number(rawSourceId)) !== true) {
    selectedPhoneSourceId.value = null;
    selectedPhoneDisplayOverride.value = '';
    emit('update:phoneSourceId', null);
    const current = Array.isArray(props.selectedChannels) === true ? props.selectedChannels.slice() : [];
    const index = current.indexOf('Phone');
    if (index > -1) {
      current.splice(index, 1);
    }
    emit('update:selectedChannels', current);
    showPhoneModal.value = false;
    toast.success(t('deploymentChannels.phone.unbound', 'Phone unbound'));
    return;
  }
  const coerced = parseInt(String(rawSourceId).trim(), 10);
  selectedPhoneSourceId.value = coerced;
  selectedPhoneDisplayOverride.value = typeof payload.number === 'string' ? payload.number : '';
  emit('update:phoneSourceId', selectedPhoneSourceId.value);
  const current = Array.isArray(props.selectedChannels) === true ? props.selectedChannels.slice() : [];
  if (current.includes('Phone') === false) {
    current.push('Phone');
  }
  emit('update:selectedChannels', current);
  showPhoneModal.value = false;
  toast.success(t('deploymentChannels.phone.selected', 'Phone number selected'));
}

function openEmailInfo() {
  showEmailModal.value = true;
}

function closeEmailInfo() {
  showEmailModal.value = false;
}

async function onWhatsAppConfirm(payload) {
  if (isChannelEnabled(pendingWhatsappChannel.value) !== true) {
    showWhatsAppModal.value = false;
    return;
  }
  if (payload == null) {
    return;
  }
  const rawSourceId = payload.sourceId;
  if (rawSourceId === null || rawSourceId === undefined || Number.isFinite(Number(rawSourceId)) !== true) {
    selectedWhatsappSourceId.value = null;
    selectedWhatsappDisplayOverride.value = '';
    emit('update:whatsappSourceId', null);
    const current = Array.isArray(props.selectedChannels) === true ? props.selectedChannels.slice() : [];
    const whatsappIndex = current.indexOf('Whatsapp');
    if (whatsappIndex > -1) {
      current.splice(whatsappIndex, 1);
    }
    const whatsappVoiceIndex = current.indexOf('Whatsapp Voice');
    if (whatsappVoiceIndex > -1) {
      current.splice(whatsappVoiceIndex, 1);
    }
    emit('update:selectedChannels', current);
    showWhatsAppModal.value = false;
    toast.success(t('deploymentChannels.whatsapp.unbound', 'WhatsApp unbound'));
    return;
  }
  const coerced = parseInt(String(rawSourceId).trim(), 10);
  selectedWhatsappSourceId.value = coerced;
  selectedWhatsappDisplayOverride.value = typeof payload.number === 'string' ? payload.number : '';
  emit('update:whatsappSourceId', selectedWhatsappSourceId.value);
  const current = Array.isArray(props.selectedChannels) === true ? props.selectedChannels.slice() : [];
  if (current.includes(pendingWhatsappChannel.value) === false) {
    current.push(pendingWhatsappChannel.value);
  }
  emit('update:selectedChannels', current);
  showWhatsAppModal.value = false;
  toast.success(t('deploymentChannels.whatsapp.selected', 'WhatsApp number selected'));
}

async function unbindWhatsapp() {
  if (isChannelEnabled('Whatsapp') !== true) {
    return;
  }
  if (selectedWhatsappSourceId.value !== null) {
    selectedWhatsappSourceId.value = null;
    selectedWhatsappDisplayOverride.value = '';
    emit('update:whatsappSourceId', null);
    const current = Array.isArray(props.selectedChannels) === true ? props.selectedChannels.slice() : [];
    const whatsappIndex = current.indexOf('Whatsapp');
    if (whatsappIndex > -1) {
      current.splice(whatsappIndex, 1);
    }
    const whatsappVoiceIndex = current.indexOf('Whatsapp Voice');
    if (whatsappVoiceIndex > -1) {
      current.splice(whatsappVoiceIndex, 1);
    }
    emit('update:selectedChannels', current);
    toast.success(t('deploymentChannels.whatsapp.unbound', 'WhatsApp unbound'));
  }
}

watch(() => props.whatsappSourceId, (value) => {
  const normalizedValue = normalizeSourceId(value);
  if (normalizedValue !== selectedWhatsappSourceId.value) {
    selectedWhatsappSourceId.value = normalizedValue;
  }
});

watch(() => props.whatsappNumber, (value) => {
  selectedWhatsappDisplayOverride.value = typeof value === 'string' ? value.trim() : '';
});

watch(() => props.phoneSourceId, (value) => {
  const normalizedValue = normalizeSourceId(value);
  if (normalizedValue !== selectedPhoneSourceId.value) {
    selectedPhoneSourceId.value = normalizedValue;
  }
});

watch(() => props.phoneNumber, (value) => {
  selectedPhoneDisplayOverride.value = typeof value === 'string' ? value.trim() : '';
});
</script>

<style scoped>
.channel-chip-list {
  display: flex;
  flex-wrap: wrap;
  margin-top: -4px;
  margin-bottom: -4px;
}

.channel-chip {
  display: inline-flex;
  align-items: center;
  margin-top: 4px;
  margin-bottom: 4px;
  margin-inline-start: 0;
  margin-inline-end: 8px;
  border-radius: 9999px;
  background: var(--fallback-b2, #eef2f7);
  padding: 6px 10px;
  font-size: 12px;
}

.channel-chip > * + * {
  margin-inline-start: 6px;
}
</style>
